import { z } from "zod";
import { STAGES, type OutcomeClass, type Stage } from "@amber/contracts";
import type { Exclusion, ItemStage, ItemStages } from "./items.js";

export const ITEM_STATUSES = ["new", "in_progress", "waiting", "failed", "finished", "excluded"] as const;
export const itemStatusSchema = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof itemStatusSchema>;

export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  new: "New",
  in_progress: "In progress",
  waiting: "Waiting",
  failed: "Failed",
  finished: "Finished",
  excluded: "Excluded",
};

export const PRECISE_STATES = [
  "not_relevant",
  "duplicate_handled_elsewhere",
  "withdrawn",
  "attempts_exhausted",
  "unprocessable_input",
  "stopped_by_operator",
  "access_needed",
  "rate_limited",
  "budget",
  "awaiting_publication",
  "scheduled",
  "paused_global",
  "paused_stage",
  "provider_outcome_unknown",
  "route_quarantined",
  "processing",
  "retrying",
  "manual_retry",
  "queued_for_stage",
  "finished",
  "discovered",
  "added_by_editor",
] as const;
export const preciseStateSchema = z.enum(PRECISE_STATES);
export type PreciseState = z.infer<typeof preciseStateSchema>;

export const PRECISE_STATE_LABELS: Record<PreciseState, string> = {
  not_relevant: "Not relevant",
  duplicate_handled_elsewhere: "Duplicate handled elsewhere",
  withdrawn: "Withdrawn",
  attempts_exhausted: "Automatic attempts exhausted",
  unprocessable_input: "Unprocessable input",
  stopped_by_operator: "Stopped by an Operator",
  access_needed: "Access needed",
  rate_limited: "Rate limited",
  budget: "Budget",
  awaiting_publication: "Awaiting publication",
  scheduled: "Scheduled",
  paused_global: "Paused (global)",
  paused_stage: "Paused stage",
  provider_outcome_unknown: "Provider outcome unknown",
  route_quarantined: "Route quarantined",
  processing: "Processing",
  retrying: "Retrying automatically",
  manual_retry: "Manual retry",
  queued_for_stage: "Queued for the next stage",
  finished: "Finished",
  discovered: "Discovered",
  added_by_editor: "Added by an Editor",
};

/** DI-76: plain-language result of an attempt, from its outcome class. */
export const OUTCOME_LABELS: Record<OutcomeClass, string> = {
  success: "Succeeded",
  challenge: "Blocked by an anti-bot check",
  rate_limited: "Rate limited",
  quota_exhausted: "Quota exhausted",
  auth_failure: "Sign-in refused",
  credentials_unavailable: "No credentials available",
  member_only: "Member-only content",
  geo_restricted: "Geo-restricted",
  expired: "Expired at the publisher",
  deleted: "Removed by the publisher",
  not_found: "Not found",
  truncated: "Transfer cut short",
  corrupt: "Could not be decoded",
  preview_only: "Only a preview is available",
  timeout: "Timed out",
  network_error: "Network error",
  unprocessable: "Could not be processed",
  provider_error: "Provider error",
};

export const STAGE_NAMES: Record<Stage, string> = {
  discover: "Discover",
  download: "Download",
  archive: "Archive",
  transcribe: "Transcribe",
  speakers: "Speakers",
  ready: "Ready",
};

export function stageName(stage: Stage, textSource: boolean): string {
  return stage === "transcribe" && textSource ? "Extract text" : STAGE_NAMES[stage];
}

export interface StatusInput {
  readonly exclusion: Pick<Exclusion, "kind"> | null;
  readonly stages: ItemStages;
  readonly editorAdded: boolean;
}

export interface StatusResult {
  readonly status: ItemStatus;
  readonly precise: PreciseState;
  readonly stage: Stage;
}

const find = (stages: ItemStages, test: (s: ItemStage) => boolean): Stage | undefined =>
  STAGES.find((k) => test(stages[k]));

const settled = (s: ItemStage) => s.state === "done" || s.state === "not_applicable";

function runningPrecise(s: ItemStage): "retrying" | "manual_retry" | "processing" {
  if (s.state === "retry_scheduled") return "retrying";
  return s.manual_attempts_used !== undefined ? "manual_retry" : "processing";
}

export function currentStage(stages: ItemStages): Stage {
  return find(stages, (s) => !settled(s)) ?? "ready";
}

export function itemStatus(input: StatusInput): StatusResult {
  const { stages } = input;
  const at = currentStage(stages);
  // DI-105 precedence: excluded, failed, waiting, in progress, finished, new.
  if (input.exclusion) return { status: "excluded", precise: input.exclusion.kind, stage: at };
  const failed = find(stages, (s) => s.state === "failed");
  if (failed) {
    const s = stages[failed];
    return { status: "failed", precise: s.state === "failed" ? s.reason : "attempts_exhausted", stage: failed };
  }
  const waiting = find(stages, (s) => s.state === "waiting");
  if (waiting) {
    const s = stages[waiting];
    return { status: "waiting", precise: s.state === "waiting" ? s.reason : "scheduled", stage: waiting };
  }
  const running = find(stages, (s) => s.state === "running" || s.state === "retry_scheduled");
  if (running) {
    const s = stages[running];
    return { status: "in_progress", precise: runningPrecise(s), stage: running };
  }
  if (STAGES.every((k) => settled(stages[k]))) return { status: "finished", precise: "finished", stage: "ready" };
  const started = STAGES.some((k) => k !== "discover" && settled(stages[k]));
  if (started) return { status: "in_progress", precise: "queued_for_stage", stage: at };
  return { status: "new", precise: input.editorAdded ? "added_by_editor" : "discovered", stage: at };
}

export const STATUS_GROUPS = ["active", "needs_attention", "finished", "excluded"] as const;
export type StatusGroup = (typeof STATUS_GROUPS)[number];

export const STATUS_GROUP: Record<ItemStatus, StatusGroup> = {
  new: "active",
  in_progress: "active",
  failed: "needs_attention",
  waiting: "needs_attention",
  finished: "finished",
  excluded: "excluded",
};

export const STATUS_ORDER: Record<ItemStatus, number> = {
  new: 1,
  in_progress: 1,
  failed: 2,
  waiting: 3,
  finished: 4,
  excluded: 5,
};
